import { Namespace, TFunction } from 'react-i18next';
import * as Yup from 'yup';

import {
  ConditionType,
  GuestsNumberOperator,
  IsCompleted,
  IsSigned,
  StayLengthOperator,
  TimeOperator,
  TimeType,
} from 'models/automation/Triggers';

export const multiSelectOptionSchema = () =>
  Yup.object({
    value: Yup.string().required(),
    label: Yup.string().required(),
  });

export const conditionSchema = ({ t }: { t: TFunction<Namespace<'en'>> }) =>
  Yup.object({
    conditionType: Yup.mixed<ConditionType | ''>()
      .oneOf(Object.values(ConditionType))
      .transform((value) => (value === '' ? null : value))
      .required(),
    all: Yup.boolean().optional(),
    backupTimeInMilliSeconds: Yup.number().when(['conditionType', 'operator'], {
      is: (conditionType: ConditionType, operator: TimeOperator) =>
        conditionType === ConditionType.BEFORE_STAY_STARTS &&
        operator === TimeOperator.LESS_THAN_OR_EQUAL,
      then: (schema) =>
        schema
          .useNegativeZeroAsNegative()
          .useNaNAsNull()
          .integer()
          .min(0)
          .required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    channels: Yup.array()
      .of(multiSelectOptionSchema())
      .when('conditionType', {
        is: ConditionType.CHANNEL,
        then: (schema) =>
          schema.min(1, t('form.validation.required')).required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    days: Yup.number().when('conditionType', {
      is: ConditionType.STAY_LENGTH,
      then: (schema) =>
        schema
          .useNegativeZeroAsNegative()
          .useNaNAsNull()
          .integer()
          .min(0)
          .required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    daysOfWeek: Yup.array()
      .of(multiSelectOptionSchema())
      .when('conditionType', {
        is: ConditionType.DAY_OF_WEEK,
        then: (schema) =>
          schema.min(1, t('form.validation.required')).required(),
        otherwise: (schema) => schema.nullable(),
      }),
    guests: Yup.number().when('conditionType', {
      is: ConditionType.GUESTS_NUMBER,
      then: (schema) =>
        schema
          .useNegativeZeroAsNegative()
          .useNaNAsNull()
          .integer()
          .min(0)
          .required(),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    operator: Yup.mixed<
      | GuestsNumberOperator
      | IsCompleted
      | IsSigned
      | StayLengthOperator
      | TimeOperator
      | ''
    >()
      .oneOf([
        ...Object.values(GuestsNumberOperator),
        ...Object.values(IsCompleted),
        ...Object.values(IsSigned),
        ...Object.values(StayLengthOperator),
        ...Object.values(TimeOperator),
      ])
      .transform((value) => (value === '' ? null : value))
      .when('conditionType', {
        is: (conditionType: ConditionType) =>
          [
            ConditionType.AFTER_STAY_ENDS,
            ConditionType.AFTER_STAY_STARTS,
            ConditionType.AGREEMENT_SIGNED,
            ConditionType.BEFORE_STAY_ENDS,
            ConditionType.BEFORE_STAY_STARTS,
            ConditionType.BOOKING_PAID_IN_FULL,
            ConditionType.GUESTS_NUMBER,
            ConditionType.PRE_ARRIVAL_FORM_SUBMITTED,
            ConditionType.STAY_LENGTH,
          ].includes(conditionType),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    propertyUids: Yup.array()
      .of(multiSelectOptionSchema())
      .when('conditionType', {
        is: ConditionType.PROPERTY,
        then: (schema) =>
          schema.min(1, t('form.validation.required')).required(),
        otherwise: (schema) => schema.nullable().optional(),
      }),
    timeInMilliSeconds: Yup.number()
      .useNaNAsNull()
      .useNegativeZeroAsNegative()
      .integer()
      .min(0)
      .when('conditionType', {
        is: (conditionType: ConditionType) =>
          [
            ConditionType.BEFORE_STAY_STARTS,
            ConditionType.BEFORE_STAY_ENDS,
            ConditionType.AFTER_STAY_STARTS,
            ConditionType.AFTER_STAY_ENDS,
          ].includes(conditionType),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    timeType: Yup.mixed<TimeType>()
      .oneOf(Object.values(TimeType))
      .when('conditionType', {
        is: (conditionType: ConditionType) =>
          [
            ConditionType.BEFORE_STAY_STARTS,
            ConditionType.BEFORE_STAY_ENDS,
            ConditionType.AFTER_STAY_STARTS,
            ConditionType.AFTER_STAY_ENDS,
          ].includes(conditionType),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
  });

export type TriggerConditionFormValue = Yup.InferType<
  ReturnType<typeof conditionSchema>
>;

export type MultiSelectOptionFormValue = Partial<
  Yup.InferType<ReturnType<typeof multiSelectOptionSchema>>
>;
